/**
 * Facture fournisseur — numérotation:
 * - normale: 2026.001 … 2026.999 (3 chiffres), puis 2026.1000 … 2026.9999 (4 chiffres)
 * - consolidée: CON 2026.001 … même règle
 * Legacy 5-chiffres (2026.00001) reste lisible.
 */

import { formatSequenceDigits } from './sequenceDigits';

const NORMAL_RE = /^(\d{4})\.(\d{3,5})$/;
const CONSOLIDATED_RE = /^CON\s+(\d{4})\.(\d{3,5})$/i;
const LEGACY_ISO_RE = /^[A-Z]+-(\d{4})-(\d+)$/i;

export function formatNormalInvoiceNumber(year, sequence) {
  const y = Number(year);
  const seq = Math.max(1, Number(sequence) || 1);
  return `${y}.${formatSequenceDigits(seq)}`;
}

export function formatConsolidatedInvoiceNumber(year, sequence) {
  const y = Number(year);
  const seq = Math.max(1, Number(sequence) || 1);
  return `CON ${y}.${formatSequenceDigits(seq)}`;
}

export function parseNormalInvoiceNumber(value) {
  const m = String(value || '').trim().match(NORMAL_RE);
  if (!m) return null;
  return { kind: 'normal', year: parseInt(m[1], 10), sequence: parseInt(m[2], 10) };
}

export function parseConsolidatedInvoiceNumber(value) {
  const m = String(value || '').trim().match(CONSOLIDATED_RE);
  if (!m) return null;
  return { kind: 'consolidated', year: parseInt(m[1], 10), sequence: parseInt(m[2], 10) };
}

export function parseAnyInvoiceNumber(value) {
  const normal = parseNormalInvoiceNumber(value);
  if (normal) return normal;
  const consolidated = parseConsolidatedInvoiceNumber(value);
  if (consolidated) return consolidated;

  const legacy = String(value || '').trim().match(LEGACY_ISO_RE);
  if (legacy) {
    return {
      kind: 'normal',
      year: parseInt(legacy[1], 10),
      sequence: parseInt(legacy[2], 10)
    };
  }

  return null;
}

export function isValidNormalInvoiceNumber(value) {
  return parseNormalInvoiceNumber(value) !== null;
}

export function isValidConsolidatedInvoiceNumber(value) {
  return parseConsolidatedInvoiceNumber(value) !== null;
}

export function invoiceNumberSortKey(value) {
  const parsed = parseAnyInvoiceNumber(value);
  if (parsed) {
    return parsed.year * 1_000_000_000 + parsed.sequence;
  }
  const tail = String(value || '').match(/(\d+)$/);
  return tail ? parseInt(tail[1], 10) : 0;
}

export function compareInvoiceNumbers(a, b) {
  const keyA = invoiceNumberSortKey(a);
  const keyB = invoiceNumberSortKey(b);
  if (keyA !== keyB) return keyA - keyB;
  return String(a || '').localeCompare(String(b || ''));
}

export function invoiceYearFromRecord(invoice) {
  const fromNumber = parseAnyInvoiceNumber(invoice?.invoiceNumber);
  if (fromNumber?.year) return fromNumber.year;
  const dateStr = invoice?.invoiceDate || invoice?.createdAt || invoice?.date;
  const d = dateStr ? new Date(dateStr) : new Date();
  const year = d.getFullYear();
  return Number.isFinite(year) ? year : new Date().getFullYear();
}

export function normalInvoiceSequenceKey(year) {
  return `INVOICE_${year}`;
}

export function consolidatedInvoiceSequenceKey(year) {
  return `INVOICE_CON_${year}`;
}

export function bumpSequenceMapFromInvoices(invoices, sequences) {
  const map = sequences instanceof Map ? sequences : new Map(Object.entries(sequences || {}));

  for (const invoice of invoices || []) {
    const isConsolidated =
      invoice?.isConsolidated === true ||
      invoice?.type === 'consolidated' ||
      String(invoice?.type || '').includes('consolidated');

    const parsed = isConsolidated
      ? parseConsolidatedInvoiceNumber(invoice?.invoiceNumber)
      : parseNormalInvoiceNumber(invoice?.invoiceNumber);

    if (!parsed) continue;

    const key = isConsolidated
      ? consolidatedInvoiceSequenceKey(parsed.year)
      : normalInvoiceSequenceKey(parsed.year);

    const prev = map.get(key) || 0;
    if (parsed.sequence > prev) {
      map.set(key, parsed.sequence);
    }
  }

  return map;
}
